/**
 * Theme state — the light/dark/system preference and the resolved theme.
 *
 * Deliberately NOT modelled on DepartmentLayout's attribute effect. That one
 * sets `data-department` on <html> and deletes it on unmount with an ownership
 * check, which is right for something scoped to a route. The theme is global
 * and lives for the whole session: copying that pattern would produce a theme
 * that clears itself on navigation.
 *
 * The class is already on <html> before this store initialises — the inline
 * <head> script in both HTML shells put it there (see themeBoot.ts). This store
 * owns changes from that point on, and its initial state is read back from what
 * the script decided so the two can never disagree at boot.
 */

import { create } from 'zustand';
import {
  applyTheme,
  readStoredTheme,
  resolveTheme,
  systemPrefersDark,
  writeStoredTheme,
  type ResolvedTheme,
  type ThemePreference,
} from '../utils/themeBoot';

interface ThemeState {
  /** What the user chose. */
  preference: ThemePreference;
  /** What that currently renders as, after consulting the OS. */
  resolved: ResolvedTheme;
  /** Set from the UI. Persists locally and applies immediately. */
  setPreference: (preference: ThemePreference) => void;
  /**
   * Adopt a preference that arrived from Firestore (another device).
   * Does not write back — the caller owns the remote document.
   */
  adoptRemotePreference: (preference: ThemePreference) => void;
  /** Wire up the OS listener. Returns its teardown. */
  initialize: () => () => void;
}

const initialPreference = readStoredTheme();

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: initialPreference,
  resolved: resolveTheme(initialPreference, systemPrefersDark()),

  setPreference: (preference) => {
    const resolved = resolveTheme(preference, systemPrefersDark());
    writeStoredTheme(preference);
    applyTheme(resolved);
    set({ preference, resolved });
  },

  adoptRemotePreference: (preference) => {
    if (get().preference === preference) return;
    const resolved = resolveTheme(preference, systemPrefersDark());
    // Mirrored into localStorage so the next boot paints this theme without
    // waiting for Firestore — that is the whole point of the two-tier storage.
    writeStoredTheme(preference);
    applyTheme(resolved);
    set({ preference, resolved });
  },

  initialize: () => {
    // Reconcile once: the inline script and this store read the same key, but
    // the OS may have changed between the script running and React mounting.
    const { preference } = get();
    const resolved = resolveTheme(preference, systemPrefersDark());
    applyTheme(resolved);
    set({ resolved });

    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => {};
    }

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      // Only 'system' follows the OS. An explicit light/dark choice is a
      // decision to override it, and must not be silently undone.
      if (get().preference !== 'system') return;
      const next: ResolvedTheme = event.matches ? 'dark' : 'light';
      applyTheme(next);
      set({ resolved: next });
    };

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  },
}));
